import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEmpresaAuth } from '../../../contexts/EmpresaAuthContext';
import { useStaffAuth } from '../../../contexts/StaffAuthContext';
import Layout from '../../../components/shared/Layout/Layout';
import Loading from '../../../components/shared/Loading/Loading';
import Button from '../../../components/shared/Button/Button';
import StatusBadge from '../../../components/shared/StatusBadge/StatusBadge';
import { useConfirm } from '../../../components/shared/ConfirmDialog/ConfirmDialog';
import useQrCamera from '../../../hooks/useQrCamera';
import { getReservationInfo, sendQrAction } from '../../../services/qrApi';
import { StaffSessionError } from '../../../services/staffApi';
import { STAFF_ROLES } from '../../../services/empresaStaff';
import EntriesPanel from '../../../components/sales/EntriesPanel';
import SalesNav from '../../../components/sales/SalesNav';
import { formatCalendarDate, formatTimestamp } from '../../../utils/dates';
import { describeQrError } from './qrMessages';
import {
  ScannerContainer,
  ScannerCard,
  ScannerTitle,
  ScannerSection,
  ScannerArea,
  CameraBox,
  CameraMessage,
  AssignedPanel,
  Feedback,
  ScannedWarning,
  ResultSection,
  QRResult,
  ReservationDetails,
  ValidationButtons,
  RecentScans,
  ScanItem,
  ManualInput
} from './QRScanner.styles';

const READER_ID = 'qr-reader';

const getStatusColor = (status) => {
  switch (status) {
    case 'validated': return 'var(--success-dark)';
    case 'rejected': return 'var(--danger)';
    default: return 'var(--warning-dark)';
  }
};

const getStatusText = (status) => {
  switch (status) {
    case 'validated': return 'Validado';
    case 'rejected': return 'Rechazado';
    default: return 'Pendiente';
  }
};

const formatMoney = (amount) => `$${Number(amount || 0).toLocaleString()}`;

// mode="empresa": la empresa dueña (token `empresa-token`), puede validar y rechazar.
// mode="staff": miembro del staff (token `staff-token`), solo valida las
// actividades que tiene asignadas; el backend responde 403 si intenta rechazar,
// así que el botón ni se muestra.
const QRScanner = ({ mode = 'empresa' }) => {
  const isStaff = mode === 'staff';
  const navigate = useNavigate();
  const { empresa } = useEmpresaAuth();
  const staffAuth = useStaffAuth();
  const { staff } = staffAuth;
  const { confirm, dialog } = useConfirm();

  const authorized = isStaff ? Boolean(staff) : Boolean(empresa);
  const actorName = isStaff ? (staff && staff.name) : (empresa && empresa.businessName);
  const canReject = !isStaff;
  const recentKey = isStaff ? `recentScans-staff-${staff ? staff.id : ''}` : 'recentScans';

  // idle | lookup | review | working | done | problem
  const [phase, setPhase] = useState({ kind: 'idle' });
  const [recentScans, setRecentScans] = useState([]);
  const [manualCode, setManualCode] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  // `entries` de la última validación: actualiza el contador de entradas.
  const [latestEntries, setLatestEntries] = useState(null);

  // --- sesión -------------------------------------------------------------

  // Devuelve true si el error era de sesión de staff (ya se limpió el token):
  // el efecto de abajo manda al login de staff con el motivo.
  const handleSessionError = useCallback((error) => {
    if (error instanceof StaffSessionError) {
      staffAuth.endSession(error.message);
      return true;
    }
    return false;
  }, [staffAuth]);

  useEffect(() => {
    if (isStaff && !staffAuth.loading && !staff && !staffAuth.connectionError) {
      navigate('/staff/login', { replace: true });
    }
    if (!isStaff && !empresa) {
      navigate('/empresa/login', { replace: true });
    }
  }, [isStaff, staffAuth.loading, staffAuth.connectionError, staff, empresa, navigate]);

  // --- escaneos recientes -------------------------------------------------

  useEffect(() => {
    if (!authorized) return;
    try {
      const scans = JSON.parse(localStorage.getItem(recentKey) || '[]');
      setRecentScans(scans.slice(0, 5));
    } catch (e) {
      setRecentScans([]);
    }
  }, [authorized, recentKey]);

  const rememberScan = (record) => {
    try {
      const existing = JSON.parse(localStorage.getItem(recentKey) || '[]');
      existing.unshift(record);
      const kept = existing.slice(0, 20);
      localStorage.setItem(recentKey, JSON.stringify(kept));
      setRecentScans(kept.slice(0, 5));
    } catch (e) {
      // sin localStorage el historial reciente simplemente no se guarda
    }
  };

  // --- procesar un QR -----------------------------------------------------

  const problem = useCallback((error) => {
    setPhase({ kind: 'problem', ...describeQrError(error, mode) });
    // 403 de staff: sus permisos pudieron cambiar; se relee /me para mostrar
    // la lista vigente.
    if (isStaff && error.status === 403) {
      staffAuth.refresh().catch(() => {});
    }
  }, [mode, isStaff, staffAuth]);

  const processQRCode = useCallback(async (qrText) => {
    // El QR firmado tiene la forma {data: {reservationId, ...}, sig}; se acepta
    // también el formato plano por compatibilidad con códigos antiguos. La firma
    // la comprueba el backend al validar.
    let payload = null;
    try {
      const parsed = JSON.parse(qrText);
      if (parsed && typeof parsed === 'object') payload = parsed.data || parsed;
    } catch (e) {
      payload = null;
    }

    if (!payload || !payload.reservationId) {
      setPhase({
        kind: 'problem',
        title: 'Código no reconocido',
        message: 'Este código no es un QR de reserva de BokerHub.',
      });
      return;
    }

    setPhase({ kind: 'lookup' });
    try {
      const reservation = await getReservationInfo(mode, payload.reservationId);
      setPhase({ kind: 'review', reservation, qrText });
    } catch (error) {
      if (handleSessionError(error)) return;
      problem(error);
    }
  }, [mode, handleSessionError, problem]);

  // --- cámara -------------------------------------------------------------

  const camera = useQrCamera(READER_ID, processQRCode);
  const startCamera = camera.start;

  // Escaneo directo: la cámara abre al entrar y cada vez que se vuelve a
  // "esperando QR", sin buscar al usuario antes. Va en un efecto (no en el
  // manejador) porque el contenedor de la cámara se vuelve a montar al pasar a
  // phase idle y debe existir en el DOM cuando arranca.
  const waitingForQr = phase.kind === 'idle';
  useEffect(() => {
    if (authorized && waitingForQr) startCamera();
  }, [authorized, waitingForQr, startCamera]);

  const scanAnother = () => {
    setPhase({ kind: 'idle' });
    setManualCode('');
  };

  const handleManualInput = () => {
    const code = manualCode.trim();
    if (!code) return;
    setManualCode('');
    camera.stop();
    processQRCode(code);
  };

  // --- validar / rechazar -------------------------------------------------

  const runAction = async (action) => {
    if (phase.kind !== 'review') return;
    const { reservation, qrText } = phase;

    if (action === 'reject') {
      const inPerson = reservation.source === 'in_person';
      const ok = await confirm({
        title: inPerson ? '¿Anular esta venta en persona?' : '¿Rechazar esta reserva?',
        message: inPerson
          ? `Se anulará la venta #${reservation.ticketNumber} de ${reservation.customerName}. Devuelve el dinero al cliente en el lugar. Esta acción no se puede deshacer.`
          : `Se cancelará la reserva de ${reservation.customerName} y se reembolsará al cliente. Esta acción no se puede deshacer.`,
        confirmLabel: inPerson ? 'Anular venta' : 'Rechazar reserva',
        danger: true,
      });
      if (!ok) return;
    }

    setPhase({ kind: 'working', reservation, qrText });
    try {
      const result = await sendQrAction(mode, qrText, action);
      const validated = action === 'validate';
      rememberScan({
        id: Date.now(),
        reservationId: result.reservation.id,
        customerName: result.reservation.customer ? result.reservation.customer.name : reservation.customerName,
        activityName: result.reservation.activityName || reservation.activityTitle,
        amount: formatMoney(result.reservation.finalAmount),
        status: validated ? 'validated' : 'rejected',
        scannedAt: new Date().toISOString(),
        scannedBy: actorName
      });
      const entries = validated ? result.entries || null : null;
      if (validated) {
        setLatestEntries({
          activityId: result.reservation.activityId,
          entries,
          nonce: Date.now(),
        });
      }
      setPhase({
        kind: 'done',
        validated,
        message: result.message || (validated ? 'Reserva validada.' : 'Reserva rechazada.'),
        reservation: result.reservation,
        entries,
      });
    } catch (error) {
      if (handleSessionError(error)) return;
      problem(error);
    }
  };

  // --- permisos del staff -------------------------------------------------

  const refreshPermissions = async () => {
    setRefreshing(true);
    try {
      await staffAuth.refresh();
    } catch (e) {
      // se conserva lo último conocido; el error de red ya queda en el contexto
    } finally {
      setRefreshing(false);
    }
  };

  const assignedList = staff && staff.activities ? staff.activities : [];

  // --- render -------------------------------------------------------------

  if (isStaff && staffAuth.loading) {
    return (
      <Layout headerType="staff">
        <Loading text="Verificando tu acceso..." />
      </Layout>
    );
  }

  if (isStaff && !staff && staffAuth.connectionError) {
    return (
      <Layout headerType="staff">
        <ScannerContainer>
          <ScannerCard>
            <Feedback $tone="danger" role="alert">
              <h3>No pudimos verificar tu acceso</h3>
              <p>{staffAuth.connectionError}</p>
              <div className="feedback-actions">
                <Button onClick={() => staffAuth.refresh().catch(() => {})}>Reintentar</Button>
              </div>
            </Feedback>
          </ScannerCard>
        </ScannerContainer>
      </Layout>
    );
  }

  if (!authorized) {
    return <Loading text="Cargando..." />;
  }

  const scanning = camera.status === 'scanning' || camera.status === 'starting';
  const busy = phase.kind === 'lookup' || phase.kind === 'working';
  const showCameraSection = phase.kind === 'idle' || phase.kind === 'lookup';
  const reservation = phase.reservation;
  const isInPerson = Boolean(reservation && reservation.source === 'in_person');
  const canSellHere = !isStaff || Boolean(staff && (staff.permissions ? staff.permissions.sell : staff.role === 'operator'));
  const canValidate = phase.kind === 'review'
    && !reservation.alreadyScanned
    && reservation.status !== 'completed'
    && reservation.status !== 'cancelled';

  return (
    <Layout headerType={isStaff ? 'staff' : 'empresa'}>
      <ScannerContainer>
        <ScannerCard>
          <ScannerTitle>Validar Códigos QR</ScannerTitle>
          <p>
            {isStaff
              ? 'Apunta la cámara al QR del cliente. Solo puedes validar las actividades que tienes asignadas.'
              : 'Apunta la cámara al QR del cliente para validar su reserva.'}
          </p>

          {canSellHere && <SalesNav mode={mode} />}

          {isStaff && (
            <AssignedPanel>
              <div className="assigned-head">
                <h3>
                  {staffAuth.empresa ? staffAuth.empresa.businessName : 'Tu empresa'}
                  {' · '}
                  <span className="assigned-role">
                    {STAFF_ROLES[staff.role] ? STAFF_ROLES[staff.role].label : staff.role}
                  </span>
                </h3>
                <Button size="small" variant="secondary" onClick={refreshPermissions} disabled={refreshing}>
                  {refreshing ? 'Actualizando…' : 'Actualizar permisos'}
                </Button>
              </div>
              {assignedList.length > 0 ? (
                <ul aria-label="Actividades asignadas">
                  {assignedList.map((a) => <li key={a.id}>{a.title}</li>)}
                </ul>
              ) : (
                <p className="assigned-empty">
                  No tienes actividades asignadas, así que no puedes escanear ningún QR. Pide a la empresa que te asigne alguna.
                </p>
              )}
            </AssignedPanel>
          )}

          {showCameraSection && (
            <ScannerSection>
              <ScannerArea>
                <CameraBox $active={scanning}>
                  <div id={READER_ID}></div>
                </CameraBox>

                {camera.status === 'starting' && <CameraMessage>Abriendo la cámara…</CameraMessage>}
                {phase.kind === 'lookup' && <CameraMessage>Buscando la reserva…</CameraMessage>}
                {camera.status === 'error' && (
                  <CameraMessage role="alert">
                    <div className="scanner-icon"><img src="/assets/logos/isotipo-blanco.png" alt="" /></div>
                    {camera.error}
                  </CameraMessage>
                )}
                {camera.status === 'idle' && phase.kind === 'idle' && (
                  <CameraMessage>
                    <div className="scanner-icon"><img src="/assets/logos/isotipo-blanco.png" alt="" /></div>
                    La cámara está apagada.
                  </CameraMessage>
                )}

                <div className="scanner-controls">
                  {scanning ? (
                    <Button variant="danger" onClick={camera.stop} disabled={camera.status === 'starting'}>
                      Detener cámara
                    </Button>
                  ) : (
                    <Button onClick={camera.start} disabled={busy}>
                      {camera.status === 'error' ? 'Reintentar cámara' : 'Activar cámara'}
                    </Button>
                  )}
                </div>
              </ScannerArea>

              <ManualInput>
                <h3>Ingreso Manual</h3>
                <p>Si no puedes escanear, pega el código del QR:</p>
                <div className="manual-input-group">
                  <input
                    type="text"
                    value={manualCode}
                    onChange={(e) => setManualCode(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleManualInput(); }}
                    placeholder="Pega el código QR aquí"
                    aria-label="Código QR"
                  />
                  <Button onClick={handleManualInput} disabled={busy || !manualCode.trim()}>Validar</Button>
                </div>
              </ManualInput>
            </ScannerSection>
          )}

          {phase.kind === 'problem' && (
            <Feedback $tone="danger" role="alert">
              <h3>{phase.title}</h3>
              <p>{phase.message}</p>
              {phase.hint === 'staff-activities' && (
                <div className="feedback-assigned">
                  {assignedList.length > 0 ? (
                    <>
                      <strong>Tus actividades asignadas:</strong>{' '}
                      {assignedList.map((a) => a.title).join(', ')}
                    </>
                  ) : (
                    <strong>Ahora mismo no tienes ninguna actividad asignada.</strong>
                  )}
                </div>
              )}
              <div className="feedback-actions">
                <Button onClick={scanAnother}>Escanear otro</Button>
              </div>
            </Feedback>
          )}

          {phase.kind === 'done' && (
            <Feedback $tone={phase.validated ? 'success' : 'neutral'} role="status">
              <h3>{phase.validated ? '✅ Reserva validada' : 'Reserva rechazada'}</h3>
              <p>{phase.message}</p>
              <p>
                {phase.reservation.customer ? phase.reservation.customer.name : ''}
                {phase.reservation.activityName ? ` · ${phase.reservation.activityName}` : ''}
              </p>
              {phase.validated && phase.entries && (
                <p>
                  <strong>Hoy han entrado {phase.entries.entered.people} de {phase.entries.expected.people} personas</strong>
                  {' '}({phase.entries.entered.coupons} de {phase.entries.expected.coupons} cupones) en esta actividad.
                </p>
              )}
              <div className="feedback-actions">
                <Button onClick={scanAnother}>Escanear otro</Button>
              </div>
            </Feedback>
          )}

          {(phase.kind === 'review' || phase.kind === 'working') && (
            <ResultSection>
              <QRResult>
                <h3>✅ Código QR Detectado</h3>
                <p>Revisa la reserva antes de validar:</p>
                {reservation.alreadyScanned && (
                  <ScannedWarning>
                    ⚠️ Esta reserva ya fue escaneada el {formatTimestamp(reservation.scannedAt)}
                    {reservation.scannedBy ? ` por ${reservation.scannedBy}` : ''}
                  </ScannedWarning>
                )}
              </QRResult>

              <ReservationDetails>
                {isInPerson && (
                  <div className="detail-row">
                    <span>Origen:</span>
                    <span>Venta en persona{reservation.ticketNumber ? ` · Ticket #${reservation.ticketNumber}` : ''}</span>
                  </div>
                )}
                <div className="detail-row">
                  <span>ID Reserva:</span>
                  <span>{reservation.id}</span>
                </div>
                <div className="detail-row">
                  <span>Cliente:</span>
                  <span>{reservation.customerName}</span>
                </div>
                <div className="detail-row">
                  <span>Actividad:</span>
                  <span>{reservation.activityTitle}</span>
                </div>
                <div className="detail-row">
                  <span>Fecha:</span>
                  <span>{formatCalendarDate(reservation.date)}</span>
                </div>
                <div className="detail-row">
                  <span>Hora:</span>
                  <span>{reservation.time}</span>
                </div>
                <div className="detail-row">
                  <span>Cantidad:</span>
                  <span>{reservation.quantity} persona(s)</span>
                </div>
                {Array.isArray(reservation.attendees) && reservation.attendees.length > 0 && (
                  <div className="detail-row">
                    <span>Asistentes:</span>
                    <span>{reservation.attendees.join(', ')}</span>
                  </div>
                )}
                <div className="detail-row">
                  <span>{isInPerson ? 'Cobrado en persona:' : 'Monto:'}</span>
                  <span style={{ color: 'var(--success-dark)', fontWeight: 'bold' }}>
                    {formatMoney(reservation.finalAmount)}
                  </span>
                </div>
                <div className="detail-row">
                  <span>Estado:</span>
                  <span><StatusBadge status={reservation.status} /></span>
                </div>
              </ReservationDetails>

              {isInPerson && (
                <p style={{ margin: '12px 0', fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                  Venta en persona: el QR solo valida la entrada. No se cobra nada al escanear.
                </p>
              )}

              <ValidationButtons>
                <Button
                  variant="success"
                  onClick={() => runAction('validate')}
                  disabled={!canValidate || phase.kind === 'working'}
                >
                  {phase.kind === 'working' ? 'Procesando…' : isInPerson ? '✅ Validar entrada' : '✅ Validar y Liberar Pago'}
                </Button>
                {canReject && (
                  <Button
                    variant="danger"
                    onClick={() => runAction('reject')}
                    disabled={!canValidate || phase.kind === 'working'}
                  >
                    {isInPerson ? '❌ Anular venta' : '❌ Rechazar Reserva'}
                  </Button>
                )}
                <Button
                  variant="ghost"
                  onClick={scanAnother}
                  disabled={phase.kind === 'working'}
                >
                  Cancelar
                </Button>
              </ValidationButtons>
            </ResultSection>
          )}

          <EntriesPanel mode={mode} latest={latestEntries} />

          <RecentScans>
            <h3>Escaneos Recientes</h3>
            {recentScans.length > 0 ? (
              recentScans.map(scan => (
                <ScanItem key={scan.id}>
                  <div className="scan-info">
                    <div className="scan-customer">{scan.customerName}</div>
                    <div className="scan-details">
                      {scan.activityName} • {scan.amount}
                    </div>
                    <div className="scan-time">
                      {new Date(scan.scannedAt).toLocaleString()}
                    </div>
                  </div>
                  <div
                    className="scan-status"
                    style={{ color: getStatusColor(scan.status) }}
                  >
                    {getStatusText(scan.status)}
                  </div>
                </ScanItem>
              ))
            ) : (
              <p style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
                No hay escaneos recientes
              </p>
            )}
          </RecentScans>
        </ScannerCard>
      </ScannerContainer>
      {dialog}
    </Layout>
  );
};

export default QRScanner;
